export const HOLD_INITIAL_SECONDS = 60;
export const HOLD_EXTRA_SECONDS = 120;
export const HOLD_PAY_LOCK_SECONDS = 10;

export const EXTRA_TIME_COPY = Object.freeze({
  title: "Need more time?",
  body: "Checkout is about to expire. You can have 2 more minutes to finish paying, or release the court.",
  confirm: "Add 2 minutes",
  cancel: "Release reservation",
  expired: "A timeout has been reached. We have canceled the reservation.",
});

export function holdStorageKey(clubId, userId) {
  return `sportmingle.reservationHold.${clubId}.${userId}`;
}

export function readHoldRecord(clubId, userId) {
  if (!clubId || !userId || typeof localStorage === "undefined") {
    return null;
  }
  try {
    return JSON.parse(localStorage.getItem(holdStorageKey(clubId, userId)) || "null");
  } catch (error) {
    return null;
  }
}

export function writeHoldRecord(clubId, userId, record) {
  if (!clubId || !userId || typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(holdStorageKey(clubId, userId), JSON.stringify(record));
}

export function clearHoldRecord(clubId, userId) {
  if (!clubId || !userId || typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(holdStorageKey(clubId, userId));
}

export function formatHoldClock(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function remainingHoldSeconds({ createdDate, extraGrantedAt, now = Date.now() } = {}) {
  if (extraGrantedAt) {
    const granted = new Date(extraGrantedAt).getTime();
    if (!Number.isFinite(granted)) {
      return 0;
    }
    return Math.max(0, HOLD_EXTRA_SECONDS - Math.floor((now - granted) / 1000));
  }
  const created = new Date(createdDate).getTime();
  if (!Number.isFinite(created)) {
    return 0;
  }
  return Math.max(0, HOLD_INITIAL_SECONDS - Math.floor((now - created) / 1000));
}

export function shouldOfferExtraTime({ createdDate, extraGrantedAt, now = Date.now() } = {}) {
  return !extraGrantedAt && remainingHoldSeconds({ createdDate, extraGrantedAt: null, now }) <= 0;
}

export function holdPhaseSeconds(extraGrantedAt) {
  return extraGrantedAt ? HOLD_EXTRA_SECONDS : HOLD_INITIAL_SECONDS;
}

export function startHoldFromTimerResponse({ reservations = [], userId, clubId, now = Date.now() } = {}) {
  const reservation = reservations[0];
  if (!reservation?.createdDate) {
    return { active: false };
  }
  if (String(reservation.reservationCreatedBy) !== String(userId)) {
    return { active: false };
  }
  const stored = readHoldRecord(clubId, userId);
  const extraGrantedAt = stored && String(stored.createdDate) === String(reservation.createdDate)
    ? stored.extraGrantedAt
    : null;
  if (shouldOfferExtraTime({ createdDate: reservation.createdDate, extraGrantedAt, now })) {
    return {
      active: true,
      askExtra: true,
      remaining: 0,
      createdDate: reservation.createdDate,
      extraGrantedAt: null,
      phaseSeconds: HOLD_INITIAL_SECONDS,
    };
  }
  const remaining = remainingHoldSeconds({
    createdDate: reservation.createdDate,
    extraGrantedAt,
    now,
  });
  if (remaining <= 0 && extraGrantedAt) {
    return {
      active: false,
      expired: true,
      createdDate: reservation.createdDate,
      extraGrantedAt,
      remaining: 0,
      phaseSeconds: HOLD_EXTRA_SECONDS,
    };
  }
  return {
    active: remaining > 0,
    askExtra: false,
    remaining,
    createdDate: reservation.createdDate,
    extraGrantedAt,
    phaseSeconds: holdPhaseSeconds(extraGrantedAt),
  };
}
